import { Controller } from "@hotwired/stimulus";

// Handles 6-digit OTP input with auto-focus and keyboard navigation
export default class extends Controller {
  static targets = ["digit", "submit"];

  connect() {
    // Focus first input on load
    if (this.digitTargets.length > 0) {
      this.digitTargets[0].focus();
    }
    this.updateSubmitButton();
  }

  handleInput(event) {
    const input = event.target;
    const value = input.value;

    // Validate and sanitize input to ensure only numeric digits
    if (value.length > 1) {
      // Extract the first numeric digit from the value
      const firstDigit = value.match(/^\d/);
      input.value = firstDigit ? firstDigit[0] : "";
    } else if (value.length === 1) {
      // Only accept if it's a numeric digit, otherwise clear
      if (!/^\d$/.test(value)) {
        input.value = "";
      }
    }

    // Move to next input if value entered
    if (input.value.length === 1) {
      const nextInput = this.getNextInput(input);
      if (nextInput) {
        nextInput.focus();
      } else {
        // Last digit entered - auto-submit if all filled
        if (this.allDigitsFilled()) {
          this.element.requestSubmit();
        }
      }
    }

    this.updateSubmitButton();
  }

  handleKeydown(event) {
    const input = event.target;

    // Handle backspace
    if (event.key === "Backspace" && input.value === "") {
      event.preventDefault();
      const prevInput = this.getPreviousInput(input);
      if (prevInput) {
        prevInput.value = "";
        prevInput.focus();
      }
    }

    // Handle arrow keys for navigation
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      const prevInput = this.getPreviousInput(input);
      if (prevInput) prevInput.focus();
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      const nextInput = this.getNextInput(input);
      if (nextInput) nextInput.focus();
    }
  }

  handlePaste(event) {
    event.preventDefault();
    const pastedData = event.clipboardData.getData("text").trim();

    // Handle 6-digit code paste
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      this.digitTargets.forEach((input, index) => {
        if (digits[index]) {
          input.value = digits[index];
        }
      });
      // Focus last input
      this.digitTargets[this.digitTargets.length - 1].focus();
      this.updateSubmitButton();

      // Auto-submit if all filled
      if (this.allDigitsFilled()) {
        this.element.requestSubmit();
      }
    }
  }

  updateSubmitButton() {
    const allFilled = this.allDigitsFilled();
    if (this.hasSubmitTarget) {
      const expired = this.element.dataset.expiredDisabled === 'true';
      this.submitTarget.disabled = !allFilled || expired;
    }
  }

  allDigitsFilled() {
    return this.digitTargets.every((input) => input.value.length === 1);
  }

  getNextInput(currentInput) {
    const pos = this.digitTargets.indexOf(currentInput);
    if (pos === -1) return null;
    return this.digitTargets[pos + 1] || null;
  }

  getPreviousInput(currentInput) {
    const pos = this.digitTargets.indexOf(currentInput);
    if (pos === -1) return null;
    return this.digitTargets[pos - 1] || null;
  }
}
